import { IFilterOption } from "@faire/web-api/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";

export const SEE_MORE_THRESHOLD = 3;
export const SEE_MORE_STEP = 10;

const DEFAULT_SHOW_ALL_FILTER_SECTIONS = [
  IFilterSection.FieldName.ORDER_STATUS,
];

const bringToggleFilterToFront = (
  a: IFilterSection,
  b: IFilterSection
): 1 | -1 | 0 => {
  if (
    a.selection_type === IFilterSection.SelectionType.TOGGLE &&
    b.selection_type !== IFilterSection.SelectionType.TOGGLE
  ) {
    return -1;
  }
  return 0;
};

export const getSortedFilterSections = (
  filterSections: ReadonlyArray<IFilterSection>,
  includeSortSection?: boolean
): ReadonlyArray<IFilterSection> => {
  if (includeSortSection) {
    return [...filterSections]
      .filter(
        (filterSection) =>
          filterSection.selection_type !==
          IFilterSection.SelectionType.MULTIPLE_OPTIONAL_SELECTIONS
      )
      .sort(bringToggleFilterToFront);
  }

  return filterSections
    .filter((filterSection) => {
      return (
        filterSection.field_function !== IFilterSection.FieldFunction.SORT &&
        filterSection.selection_type !==
        IFilterSection.SelectionType.MULTIPLE_OPTIONAL_SELECTIONS
      );
    })
    .sort(bringToggleFilterToFront);
};

export const getToggleFilters = (
  filterSections: ReadonlyArray<IFilterSection>
): IFilterSection[] => {
  return filterSections.filter(
    (filterSection) =>
      filterSection.selection_type === IFilterSection.SelectionType.TOGGLE
  );
};

export const getMultipleOptionalSelectionsFilters = (
  filterSections: ReadonlyArray<IFilterSection>
): IFilterSection[] => {
  return filterSections.filter(
    (filterSection) =>
      filterSection.selection_type ===
      IFilterSection.SelectionType.MULTIPLE_OPTIONAL_SELECTIONS
  );
};

const recursivelySyncMultipleOptionalSelectionsFilters = (
  option: IFilterOption,
  multipleOptionalSelectionsFilters: IFilterSection[]
): void => {
  multipleOptionalSelectionsFilters.forEach((m_section) => {
    m_section.options.forEach((m_option) => {
      if (m_option.key === option.key) {
        m_option.is_selected = option.is_selected;
      }
    });
  });
  option.sub_options.forEach((subOption) => {
    recursivelySyncMultipleOptionalSelectionsFilters(
      subOption,
      multipleOptionalSelectionsFilters
    );
  });
};

export const syncToggleFilters = (
  section: IFilterSection,
  filterSections: ReadonlyArray<IFilterSection>
): void => {
  const toggleFilterSections = getToggleFilters(filterSections);
  const multipleOptionalSelectionsFilters =
    getMultipleOptionalSelectionsFilters(filterSections);
  section.options.forEach((option) => {
    recursivelySyncToggleFilters(option, toggleFilterSections);
    recursivelySyncMultipleOptionalSelectionsFilters(
      option,
      multipleOptionalSelectionsFilters
    );
  });
};

const recursivelySyncToggleFilters = (
  option: IFilterOption,
  toggleFilterSections: IFilterSection[]
): void => {
  toggleFilterSections.forEach((section) => {
    const firstOption =
      section.options.length > 0 ? section.options[0] : undefined;
    if (firstOption && firstOption.key === option.key) {
      firstOption.is_selected = option.is_selected;
    }
  });
  option.sub_options.forEach((subOption) => {
    recursivelySyncToggleFilters(subOption, toggleFilterSections);
  });
};

export const updateAllFilterKeys = (
  filterSections: ReadonlyArray<IFilterSection>,
  filterKey: string,
  value: boolean
): ReadonlyArray<IFilterSection> => {
  filterSections.forEach((section: IFilterSection) => {
    section.options.forEach((option: IFilterOption) => {
      recursivelyToggleFilter(option, filterKey, value);
    });
  });
  return filterSections;
};

export const DEFAULT_OPTION_GROUP_NAME = "default_option_group";

export const getFilteredOptionsGroupMap = (
  filterSection: IFilterSection,
  searchValue: string,
  optionsLimit?: number
): {
  optionsGroupMap: { [key: string]: IFilterOption[] };
  totalFilteredOptions: number;
} => {
  const optionsGroupMap: { [key: string]: IFilterOption[] } = {
    [DEFAULT_OPTION_GROUP_NAME]: [],
  };
  const filteredOptions = filterSection.options.filter(
    (option: IFilterOption) => {
      if (searchValue === "" || matchesSearchValue(option, searchValue)) {
        return true;
      }
      return false;
    }
  );
  filteredOptions.slice(0, optionsLimit).forEach((option) => {
    const group = filterSection.option_groups.find(
      (optionGroup) => optionGroup.key === option.group_key
    );
    const groupName = group?.display_name ?? DEFAULT_OPTION_GROUP_NAME;
    const options = optionsGroupMap[groupName] ?? [];
    options.push(option);
    optionsGroupMap[groupName] = options;
  });
  return { optionsGroupMap, totalFilteredOptions: filteredOptions.length };
};

export const getFilterSection = (
  options: ReadonlyArray<IFilterSection>,
  fieldNameToGet: keyof typeof IFilterSection.FieldName
) => options.find((option) => option.field_name === fieldNameToGet);

export const getFilteredOptions = (
  options: IFilterOption[],
  searchValue: string
): IFilterOption[] => {
  if (searchValue === "") {
    return options;
  }
  return options.filter((option) => matchesSearchValue(option, searchValue));
};

const matchesSearchValue = (
  option: IFilterOption,
  searchValue: string
): boolean => {
  const optionMatches = option.display_name
    ?.toLowerCase()
    .includes(searchValue.toLowerCase());
  return (
    optionMatches ||
    option.sub_options.some((option: IFilterOption) =>
      matchesSearchValue(option, searchValue)
    )
  );
};

export const isDefaultShowAllFilterSection = (
  filterSection: IFilterSection
): boolean => {
  return DEFAULT_SHOW_ALL_FILTER_SECTIONS.includes(
    IFilterSection.FieldName[filterSection.field_name ?? "FIELD_NAME_UNKNOWN"]
  );
};

export const hasSelectedOptions = (filterOption?: IFilterOption): boolean => {
  return (
    !!filterOption?.is_selected ||
    !!filterOption?.sub_options?.some(hasSelectedOptions)
  );
};

export const clearAllFilters = (
  filterSections: ReadonlyArray<IFilterSection>
): void => {
  filterSections.forEach((section) => {
    clearFilterSection(section);
  });
};

export const clearFilterSection = (filterSection: IFilterSection): void => {
  filterSection.options.forEach((option) => {
    recursivelyClearFilter(option);
  });
};

export const recursivelyClearFilter = (
  filterOption: IFilterOption,
  filterKey?: string
): void => {
  filterOption.sub_options?.forEach((subOption) => {
    recursivelyClearFilter(subOption, filterKey);
  });
  if (filterOption.key !== filterKey) {
    filterOption.is_selected = false;
    filterOption.selection_state = IFilterOption.SelectionState.UNSELECTED;
  }
};

export const recursivelyToggleFilter = (
  filterOption: IFilterOption,
  filterKey: string,
  value: boolean
): void => {
  filterOption.sub_options?.forEach((subOption) => {
    recursivelyToggleFilter(subOption, filterKey, value);
  });
  if (filterOption.key === filterKey) {
    filterOption.is_selected = value;
  }
};

export const updateAllSelectableSubOptions = (
  filterOption?: IFilterOption
): void => {
  if (!filterOption) {
    return;
  }
  selectableSubOptionsOf(filterOption).forEach((subOption) => {
    subOption.is_selected = filterOption.is_selected;
    updateAllSelectableSubOptions(subOption);
  });
};

const selectableSubOptionsOf = (
  filterOption: IFilterOption
): IFilterOption[] => {
  return (
    filterOption.sub_options?.filter(
      (subOption) => (subOption.results_count ?? 0) > 0 || subOption.is_selected
    ) ?? []
  );
};

export const selectIfSelectedAllSubOptions = (
  filterOption?: IFilterOption
): void => {
  if (!filterOption) {
    return;
  }
  const selectableSubOptions = selectableSubOptionsOf(filterOption);
  if (selectableSubOptions.length > 0) {
    selectableSubOptions.forEach(selectIfSelectedAllSubOptions);
    filterOption.is_selected = selectableSubOptions.every(
      (subOption) => !!subOption.is_selected
    );
  }
};

export const getSelectedOptions = (
  filterSection: IFilterSection
): IFilterOption[] => {
  return filterSection.options.flatMap<IFilterOption>(
    recursivelyGetSelectedOptions
  );
};

const recursivelyGetSelectedOptions = (
  option: IFilterOption
): IFilterOption[] => {
  const selectedOptions: IFilterOption[] = [];
  if (option.is_selected) {
    selectedOptions.push(option);
  }
  return selectedOptions.concat(
    option.sub_options.flatMap<IFilterOption>(recursivelyGetSelectedOptions)
  );
};

export const getSelectedFiltersCount = (
  filterSections: ReadonlyArray<IFilterSection>
): number => {
  let count = 0;
  filterSections
    .filter(
      (section) =>
        section.selection_type !==
        IFilterSection.SelectionType.MULTIPLE_OPTIONAL_SELECTIONS &&
        section.field_function !== IFilterSection.FieldFunction.SORT
    )
    .forEach((section) => {
      section.options.forEach((option) => {
        count += recursivelyGetSelectedFiltersCount(option);
      });
    });
  return count;
};

const recursivelyGetSelectedFiltersCount = (option: IFilterOption): number => {
  if (option.sub_options.length === 0) {
    return option.selection_state ===
      IFilterOption.SelectionState.FULLY_SELECTED || option.is_selected
      ? 1
      : 0;
  }
  return option.sub_options.reduce(
    (count, subOption) => count + recursivelyGetSelectedFiltersCount(subOption),
    0
  );
};

export const getFirstFilterSectionsWithTooltip = (
  filterSections: ReadonlyArray<IFilterSection>
): IFilterSection | undefined => {
  let section: IFilterSection | undefined;
  filterSections.every((filterSection) => {
    if (filterSection.tooltip !== undefined) {
      section = filterSection;
      return false;
    }
    return true;
  });
  return section;
};
